import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const VISIBILITY_DURATION = 1000
const FLUSH_INTERVAL = 5000
const MAX_BATCH_SIZE = 50
const MAX_ATTEMPTS = 3

export default class extends Controller {
    static targets = ["item"]
    static values = { endpoint: String }

    initialize() {
        this.visibilityTimers = new Map()
        this.visibleItems = new Set()
        this.pending = new Map()
        this.recordedIds = new Set()
        this.discardedIds = new Set()
        this.inFlightIds = new Set()
        this.updateVisibility = this.updateVisibility.bind(this)
        this.flushOnPageHide = this.flushOnPageHide.bind(this)
    }

    connect() {
        if (document.documentElement.hasAttribute("data-turbo-preview")) return
        this.connected = true
        if (typeof IntersectionObserver !== "undefined") this.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                    this.visibleItems.add(entry.target)
                    this.startVisibilityTimer(entry.target)
                } else {
                    this.visibleItems.delete(entry.target)
                    this.cancelVisibilityTimer(entry.target)
                }
            })
        }, { threshold: [0, 0.5] })
        this.itemTargets.forEach(item => this.observer?.observe(item))
        document.addEventListener("visibilitychange", this.updateVisibility)
        this.element.addEventListener("community:tab-change", this.updateVisibility)
        window.addEventListener("pagehide", this.flushOnPageHide)
        document.addEventListener("turbo:before-cache", this.flushOnPageHide)
        this.flushInterval = setInterval(() => this.flush(), FLUSH_INTERVAL)
    }

    disconnect() {
        this.connected = false
        this.observer?.disconnect()
        this.visibilityTimers.forEach(clearTimeout)
        this.visibilityTimers.clear()
        this.visibleItems.clear()
        clearInterval(this.flushInterval)
        document.removeEventListener("visibilitychange", this.updateVisibility)
        this.element.removeEventListener("community:tab-change", this.updateVisibility)
        window.removeEventListener("pagehide", this.flushOnPageHide)
        document.removeEventListener("turbo:before-cache", this.flushOnPageHide)
        this.finishRefreshWait?.()
        this.finishRefreshNavigation?.()
        this.requestController?.abort()
    }

    itemTargetConnected(item) {
        this.observer?.observe(item)
    }

    itemTargetDisconnected(item) {
        this.observer?.unobserve(item)
        this.visibleItems.delete(item)
        this.cancelVisibilityTimer(item)
    }

    updateVisibility() {
        // Switching tabs or leaving the document interrupts continuous exposure.
        this.visibilityTimers.forEach(clearTimeout)
        this.visibilityTimers.clear()
        this.visibleItems.clear()
        this.itemTargets.forEach(item => {
            this.observer?.unobserve(item)
            this.observer?.observe(item)
        })
    }

    isActive(item) {
        const tab = item.closest("[data-feed-tab]")
        return this.connected && !document.hidden &&
            !document.documentElement.hasAttribute("data-turbo-preview") &&
            item.isConnected && tab?.dataset.feedTab === this.element.dataset.activeTab
    }

    startVisibilityTimer(item) {
        const id = Number(item.dataset.feedItemId)
        if (!this.isActive(item) || !Number.isSafeInteger(id) || id <= 0 ||
            item.dataset.feedImpressionRecorded === "true" || this.recordedIds.has(id) ||
            this.discardedIds.has(id) ||
            this.pending.has(id) || this.inFlightIds.has(id) || this.visibilityTimers.has(item)) return

        this.visibilityTimers.set(item, setTimeout(() => {
            this.visibilityTimers.delete(item)
            if (this.isActive(item) && this.visibleItems.has(item) &&
                !this.pending.has(id) && !this.inFlightIds.has(id) &&
                !this.recordedIds.has(id) && !this.discardedIds.has(id)) {
                this.pending.set(id, { attempts: 0, retryAt: 0 })
            }
        }, VISIBILITY_DURATION))
    }

    cancelVisibilityTimer(item) {
        clearTimeout(this.visibilityTimers.get(item))
        this.visibilityTimers.delete(item)
    }

    async flush() {
        if (this.flushPromise) return this.flushPromise
        if (!this.connected || !this.hasEndpointValue) return
        const ids = Array.from(this.pending.keys()).filter(id => this.pending.get(id).retryAt <= Date.now()).slice(0, MAX_BATCH_SIZE)
        if (ids.length === 0) return

        ids.forEach(id => {
            this.inFlightIds.add(id)
            this.pending.get(id).attempts += 1
        })
        this.requestController = new AbortController()
        const timeout = setTimeout(() => this.requestController?.abort(), 8000)
        this.flushPromise = this.submit(ids, { signal: this.requestController.signal }).then(response => {
            if (response.ok) {
                ids.forEach(id => {
                    this.recordedIds.add(id)
                    this.pending.delete(id)
                })
                this.itemTargets.forEach(item => {
                    if (this.recordedIds.has(Number(item.dataset.feedItemId))) item.dataset.feedImpressionRecorded = "true"
                })
            } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
                ids.forEach(id => {
                    this.pending.delete(id)
                    this.discardedIds.add(id)
                })
            } else {
                this.scheduleRetry(ids)
            }
        }).catch(() => this.scheduleRetry(ids)).finally(() => {
            clearTimeout(timeout)
            ids.forEach(id => this.inFlightIds.delete(id))
            this.flushPromise = null
            this.requestController = null
        })
        return this.flushPromise
    }

    scheduleRetry(ids) {
        ids.forEach(id => {
            const state = this.pending.get(id)
            if (!state) return
            if (state.attempts >= MAX_ATTEMPTS) {
                this.pending.delete(id)
                // Keep exhausted items from starting another retry cycle while still visible.
                this.discardedIds.add(id)
            } else {
                state.retryAt = Date.now() + FLUSH_INTERVAL * (2 ** (state.attempts - 1))
            }
        })
    }

    submit(ids, options = {}) {
        return fetch(this.endpointValue, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
            },
            body: JSON.stringify({ feed_item_ids: ids }),
            ...options
        })
    }

    flushOnPageHide() {
        this.visibilityTimers.forEach(clearTimeout)
        this.visibilityTimers.clear()
        if (!this.hasEndpointValue) return
        // Navigation may abort the ordinary request; insert-once ingestion makes resending it safe.
        const ids = Array.from(this.pending.keys())
        for (let offset = 0; offset < ids.length; offset += MAX_BATCH_SIZE) {
            this.submit(ids.slice(offset, offset + MAX_BATCH_SIZE), { keepalive: true }).catch(() => {})
        }
    }

    async refresh(event) {
        if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            event.stopImmediatePropagation()
            return
        }
        event.preventDefault()
        if (this.refreshing) {
            event.stopImmediatePropagation()
            return
        }
        this.refreshing = true
        const link = event.currentTarget
        const frame = document.getElementById(link.dataset.turboFrame)
        try {
            await this.waitForFlush()
            if (!this.connected) return
            this.element.dispatchEvent(new CustomEvent("community:feed-refresh", { bubbles: true }))
            if (frame) {
                if (frame.getAttribute("src") === link.getAttribute("href")) {
                    frame.reload()
                } else {
                    frame.setAttribute("src", link.getAttribute("href"))
                }
                await this.waitForFrame(frame)
            } else {
                Turbo.visit(link.href)
            }
        } finally {
            this.refreshing = false
            link.dispatchEvent(new CustomEvent("loading-button:stop"))
        }
    }

    waitForFrame(frame) {
        return new Promise(resolve => {
            const timeout = setTimeout(() => finish(), 10000)
            const finish = () => {
                clearTimeout(timeout)
                if (this.finishRefreshNavigation === finish) this.finishRefreshNavigation = null
                resolve()
            }
            this.finishRefreshNavigation = finish
            frame.loaded.then(finish, finish)
        })
    }

    waitForFlush() {
        if (!this.hasEndpointValue) return Promise.resolve()
        return new Promise(resolve => {
            const timeout = setTimeout(() => finish(), 1200)
            const finish = () => {
                clearTimeout(timeout)
                if (this.finishRefreshWait === finish) this.finishRefreshWait = null
                resolve()
            }
            this.finishRefreshWait = finish
            const drain = async () => {
                await this.flush()
                while (this.connected && this.finishRefreshWait === finish && Array.from(this.pending.values()).some(state => state.retryAt <= Date.now())) {
                    await this.flush()
                }
                finish()
            }
            drain()
        })
    }
}
